import { Grid } from '@mui/material';
import { Box } from '@mui/system';
import { Form, Formik } from 'formik';
import { CancellationIdCardCrumbs } from 'front-office/constants/cancellation-id-card-crumbs';
import { Fragment, useCallback, useEffect, useState } from 'react';
import Breadcrumbs from 'shell/components/shared/breadcrumb';
import { StepTypography, StepButton } from 'shell/components/shared/step-button';
import IdCardForm from 'front-office/components/cancellation-id-card/form';
import { PmiButton } from 'shell/components/shared/button';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useHttpFetch } from 'shell/hooks/use-http-fetch';
import { __PERSON_NEW_API_URL__ } from 'shell/constants/environment';
import { HttpResponse } from 'shell/models/http-response';
import { ResponseAnprData } from 'shell/models/person';
import { AxiosResponse } from 'axios';
import { transformIdentityCard } from 'shell/utils/consult-person/response-transformer/documents/identity-card';
import { transformPerson } from 'shell/utils/consult-person/response-transformer/person/person';
import { AccordionPersonForm } from 'shell/view-models/accordion-person-form';
import { transformEditDataControl } from 'shell/utils/consult-person/response-transformer/edit-data-control';
import { AccordionEditDataControl } from 'shell/view-models/edit-data-control';
import { Generality } from 'shell/models/person/generality';
import { ResponsePromise } from 'shell/hooks/use-http-fetch/stateless/type';
import { toast } from 'react-toastify';
import { useUserProfile } from 'shell/hooks/use-user-profile';
import { AccordionIdentityCardVM } from 'front-office/view-models/registration-id-card';
import ExitModalRegCancIdCard from 'front-office/components/shared/exit-modal';
import { useHttpClient } from 'shell/hooks/use-http-fetch/stateless';
import { StickyDiv } from 'shell/components/shared/sticky-div';
import { useFilterTable } from 'shell/hooks/use-filter-table';
import { PersonSearchStorageVM } from 'shell/view-models/search-anpr';

export interface CancellationIdCardForm {
  personStep: AccordionPersonForm | null;
  identityCard: AccordionIdentityCardVM | undefined;
  editDataControl: AccordionEditDataControl | undefined;
  generality?: Generality;
  codicePersonaSipo?: string;
}

const transformResponse = (
  response: AxiosResponse<HttpResponse<ResponseAnprData>>
): CancellationIdCardForm => {
  const responseToTransform = response.data.payload?.risposta3002OK || {};

  const person = responseToTransform.listaSoggetti?.datiSoggetto[0] || {};

  const headingResponse = responseToTransform.testataRisposta;

  const personStep = transformPerson(person);
  const editDataControl = transformEditDataControl(headingResponse);
  const identityCard = transformIdentityCard(person);

  return {
    personStep,
    identityCard,
    editDataControl,
    generality: person.generalita,
    codicePersonaSipo: person.codicePersonaSipo,
  };
};

const CancellationIdCardPage = (): React.ReactElement => {
  const [isCancellationModalOpen, setCancellationModalOpen] = useState(false);
  const [queryParams] = useSearchParams();
  const navigate = useNavigate();

  const nationalSearch = JSON.parse(queryParams.get('ricercaNazionale') || 'false');
  const fiscalCode = queryParams.get('codiceFiscale') || '';

  const { userProfile } = useUserProfile();
  const filterTable = useFilterTable<PersonSearchStorageVM>();

  const [anpr, getAnpr] = useHttpFetch<HttpResponse<ResponseAnprData>, CancellationIdCardForm>(
    `${__PERSON_NEW_API_URL__}/persona`,
    {
      onBootstrap: false,
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {
          criteriRicerca: {
            codiceFiscale: fiscalCode || undefined,
          },
          datiRichiesta: {
            ricercaNazionale: nationalSearch ? '1' : '0',
          },
        },
      },
      transformer: (response) => transformResponse(response),
    }
  );

  const cancelIdCard = useHttpClient(
    `${__PERSON_NEW_API_URL__}/persona/carte-identita/annullamento-cid`,
    {
      options: {
        errorCatching: true,
        method: 'POST',
        loading: true,
        data: {},
      },
    }
  );

  // const [reasonRelease, getReasonRelease] = useHttpFetch<
  //   HttpResponse<{
  //     motivoRilascio: string;
  //   }>,
  //   HttpResponse<{
  //     motivoRilascio: string;
  //   }>
  // >(`${__PERSON_API_URL__}/persona/carte-identita`, {
  //   transformer: (response) => response.data,
  //   onBootstrap: false,
  //   options: {
  //     errorCatching: true,
  //     method: 'GET',
  //     loading: true,
  //   },
  // });

  useEffect(() => {
    getAnpr({
      data: {
        criteriRicerca: {
          codiceFiscale: fiscalCode || undefined,
        },
        datiRichiesta: {
          ricercaNazionale: nationalSearch ? '1' : '0',
          dataRiferimentoRichiesta:
            filterTable?.values?.filters?.subjectData?.referenceDate?.toFormat('yyyy-MM-dd') ||
            undefined,
          comuneResidenza:
            filterTable?.values?.filters?.commonSearch?.provinceOfResidence ||
            filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
            filterTable?.values?.filters?.subjectData?.provinceOfResidence ||
            filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id
              ? {
                  siglaProvinciaIstat:
                    filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.province ||
                    filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.province ||
                    undefined,
                  codiceIstat:
                    filterTable?.values?.filters?.commonSearch?.municipalityOfResidence?.id ||
                    filterTable?.values?.filters?.subjectData?.municipalityOfResidence?.id ||
                    undefined,
                  nomeComune:
                    filterTable?.values?.filters?.commonSearch?.municipalityOfResidence
                      ?.description ||
                    filterTable?.values?.filters?.subjectData?.municipalityOfResidence
                      ?.description ||
                    undefined,
                }
              : undefined,
        },
      },
    }) as ResponsePromise<HttpResponse<ResponseAnprData>, CancellationIdCardForm>;
  }, [filterTable?.values?.filters?.commonSearch, filterTable?.values?.filters?.subjectData]);

  const onCancelConfirm = useCallback(() => {
    const { identityCard, personStep, generality, codicePersonaSipo } =
      anpr.data as CancellationIdCardForm;

    (
      cancelIdCard({
        data: {
          codicePersonaSipo,
          generalitaRichiedente: generality,
          cartaIdentita: {
            matricola: personStep?.idSchedaSoggettoANPR
              ? Number.parseInt(personStep?.idSchedaSoggettoANPR)
              : undefined,
            key: {
              numero: identityCard?.number,
              ente: userProfile?.datiLogin?.key?.codiceEnte,
            },
            dataRilascio: identityCard?.releaseDate?.toFormat('yyyyMMdd'),
            dataScadenza: identityCard?.expirationDate?.toFormat('yyyyMMdd'),
            flag: identityCard?.type,
            validita: identityCard?.expatriationBan ? 'S' : 'N',
            quartRichiesta: ' ',
            // motivoRilascio: form.identityCard?.releaseMode,
          },
          comuneRilascio: !identityCard?.isConsulateRelease
            ? {
                codiceIstat: identityCard?.municipality?.istatCode,
                descrizioneLocalita: identityCard?.municipality?.locality || undefined,
                nomeComune: identityCard?.municipality?.description,
                siglaProvinciaIstat: identityCard?.municipality?.province,
              }
            : undefined,
          consolatoRilascio: identityCard?.isConsulateRelease
            ? {
                codiceConsolato: identityCard?.consulateCode,
                descrizioneConsolato: identityCard?.consulateDescription,
              }
            : undefined,
        },
      }) as ResponsePromise<void>
    ).then(() => {
      toast.success('Operazione Eseguita con successo');
      navigate('/sportello/annullamento-carta');
    });
  }, [navigate, cancelIdCard, userProfile, anpr.data]);

  const openCancellationModal = useCallback(
    () => setCancellationModalOpen(true),
    [setCancellationModalOpen]
  );

  const closeCancellationModal = useCallback(
    () => setCancellationModalOpen(false),
    [setCancellationModalOpen]
  );

  // if (!reasonRelease.data || reasonRelease.isLoading) {
  //   return <></>;
  // }

  return (
    <Fragment>
      <Breadcrumbs items={CancellationIdCardCrumbs} />
      <Formik
        initialValues={
          {
            identityCard: {
              // releaseMode: (
              //   reasonRelease.data as HttpResponse<{
              //     motivoRilascio: string;
              //   }>
              // ).payload?.motivoRilascio,
            },
          } as CancellationIdCardForm
        }
        onSubmit={onCancelConfirm}
      >
        <Form>
          <Grid container spacing={2}>
            <Grid item xs={12} md={2}>
              <StickyDiv>
                <StepButton active fullWidth>
                  <StepTypography active>Documenti</StepTypography>
                </StepButton>
                <div className="pmi-pt-4">
                  <div className="pmi-pb-2">
                    <StepButton
                      variant="outlined"
                      color="cdmBrandThird"
                      fullWidth
                      isButton
                      onClick={onCancelConfirm}
                    >
                      <Box
                        sx={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Conferma
                      </Box>
                    </StepButton>
                  </div>
                  <div className="pmi-pb-2">
                    <PmiButton
                      variant="contained"
                      color="cdmBrand"
                      fullWidth
                      type="button"
                      onClick={openCancellationModal}
                    >
                      <Box
                        sx={{
                          textOverflow: 'ellipsis',
                          overflow: 'hidden',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        Annulla Operazione
                      </Box>
                    </PmiButton>
                  </div>
                </div>
              </StickyDiv>
            </Grid>
            <Grid item xs={12} md={10}>
              {anpr.data && <IdCardForm form={anpr.data as CancellationIdCardForm} />}
            </Grid>
          </Grid>
          <ExitModalRegCancIdCard
            onClose={closeCancellationModal}
            onConfirm={() => navigate('/sportello/annullamento-carta')}
            open={isCancellationModalOpen}
          />
        </Form>
      </Formik>
    </Fragment>
  );
};

CancellationIdCardPage.displayName = 'Cancellation id card page component';

export default CancellationIdCardPage;
